import { Link } from 'react-router-dom';
import { cn } from '@/shared/lib/cn';

const DEFAULT_LOGO_SRC = '/branding/myu-logo.png';
const DEFAULT_LOGO_MARK_SRC = '/branding/myu-logo-mark.png';

const logoSrc = import.meta.env.VITE_MYU_LOGO_URL ?? DEFAULT_LOGO_SRC;
const logoMarkSrc = import.meta.env.VITE_MYU_LOGO_MARK_URL ?? DEFAULT_LOGO_MARK_SRC;

export type MyULogoVariant = 'full' | 'mark';
export type MyULogoSize = 'sm' | 'md' | 'lg' | 'sidebar' | 'auth';

/** Square icon mark — same asset for full and mark variants. */
const sizeClasses: Record<MyULogoSize, string> = {
  sm: 'h-8 w-8',
  md: 'h-9 w-9',
  lg: 'h-12 w-12 sm:h-14 sm:w-14',
  /** App sidebar — fits the 64px header row beside the top bar. */
  sidebar: 'h-11 w-11',
  auth: 'h-24 w-24 sm:h-28 sm:w-28',
};

interface MyULogoProps {
  variant?: MyULogoVariant;
  size?: MyULogoSize;
  className?: string;
  /** Wrap in a link to home (marketing) or feed (app). */
  href?: string;
}

export default function MyULogo({ variant = 'full', size = 'md', className, href }: MyULogoProps) {
  const src = variant === 'mark' ? logoMarkSrc : logoSrc;
  const dimensionClass = sizeClasses[size];

  const image = (
    <img src={src} alt="MyU" className={cn('shrink-0 object-contain', dimensionClass, className)} />
  );

  if (!href) {
    return image;
  }

  return (
    <Link
      to={href}
      className="inline-flex shrink-0 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-emphasis focus-visible:ring-offset-2"
    >
      {image}
    </Link>
  );
}
